import { CheckIcon, ChevronDownIcon, LayoutGridIcon } from 'lucide-react';
import { Link } from 'react-router';
import type { ProductCode } from '@gb-ticket/shared-types';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { ProductPictogram, ProductPictogramMark } from '@/platform/lib/product-icon';
import type { PlatformProductTile } from '@/platform/lib/products';

/** Module picker. The collapsed rail clips this row to the pictogram; the menu opens to the right. */
export function PlatformModuleSwitcher({
  tiles,
  activeCode,
  collapsed,
}: {
  tiles: readonly PlatformProductTile[];
  activeCode: ProductCode | null;
  collapsed: boolean;
}) {
  const live = tiles.filter((tile) => tile.status === 'live' && tile.href);
  const planned = tiles.filter((tile) => tile.status === 'planned');
  if (live.length === 0 && planned.length === 0) {
    return null;
  }

  const current = live.find((tile) => tile.code === activeCode) ?? null;

  return (
    <div>
      <p className="sidebar-copy px-3 pb-1.5 text-xs font-medium tracking-wide whitespace-nowrap text-muted-foreground uppercase">
        Module
      </p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={collapsed ? 'ghost' : 'outline'}
            aria-label="Module wechseln"
            title={collapsed ? (current?.labelDe ?? 'Module wechseln') : undefined}
            className={cn(
              'sidebar-hit font-normal shadow-none',
              collapsed
                ? cn('h-10 w-10 p-0', current && 'bg-accent text-primary')
                : 'h-auto w-full justify-start gap-2.5 px-2 py-1.5',
            )}
          >
            {current ? (
              collapsed ? (
                <ProductPictogram code={current.code} className="size-6" />
              ) : (
                <ProductPictogramMark code={current.code} size="sm" />
              )
            ) : (
              <span
                className={cn(
                  'inline-flex shrink-0 items-center justify-center rounded-md text-muted-foreground',
                  collapsed ? 'size-6 bg-transparent' : 'size-7 bg-muted',
                )}
              >
                <LayoutGridIcon
                  aria-hidden
                  className={collapsed ? 'size-6' : 'size-3.5'}
                  strokeWidth={1.75}
                />
              </span>
            )}
            <span className="sidebar-copy min-w-0 flex-1 text-left">
              <span className="block text-[0.65rem] font-medium tracking-wide text-muted-foreground uppercase">
                {current ? 'Aktuelles Modul' : 'Modul'}
              </span>
              <span className="block truncate text-sm font-semibold text-foreground">
                {current?.labelDe ?? 'Auswählen'}
              </span>
            </span>
            <ChevronDownIcon
              aria-hidden
              className="sidebar-copy size-4 shrink-0 text-muted-foreground"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side={collapsed ? 'right' : 'bottom'}
          sideOffset={6}
          className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56"
        >
          <DropdownMenuLabel className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Module
          </DropdownMenuLabel>
          {live.map((tile) => {
            const active = tile.code === activeCode;
            return (
              <DropdownMenuItem key={tile.code} asChild>
                <Link to={tile.href!} className={cn('gap-2.5', active && 'bg-accent')}>
                  <ProductPictogramMark code={tile.code} size="sm" />
                  <span className="min-w-0 flex-1 truncate font-medium">{tile.labelDe}</span>
                  {active ? <CheckIcon className="size-4 text-primary" aria-hidden /> : null}
                </Link>
              </DropdownMenuItem>
            );
          })}
          {planned.length > 0 ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                Demnächst
              </DropdownMenuLabel>
              {planned.map((tile) => (
                <DropdownMenuItem key={tile.code} disabled className="gap-2.5">
                  <ProductPictogramMark code={tile.code} size="sm" className="opacity-70" />
                  <span className="min-w-0 flex-1 truncate">{tile.labelDe}</span>
                </DropdownMenuItem>
              ))}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
